import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Head from 'next/head';
import Link from 'next/link';
import { truncateText } from 'pubweb-smokey/dist/utils/formatters';
import AspectImage from '@components/Shared/AspectImage/AspectImage';
import CalendarSvg from 'pubweb-smokey/dist/images/svg/iconography-16x16/calendar-blank.svg';
import HouseSvg from 'pubweb-smokey/dist/images/svg/iconography-features-48x48/modular.svg';
import BedroomSvg from 'pubweb-smokey/dist/images/svg/iconography-48x48/bed-side.svg';
import BathroomSvg from 'pubweb-smokey/dist/images/svg/iconography-48x48/bathroom.svg';
import CameraSvg from 'pubweb-smokey/dist/images/svg/iconography-16x16/camera.svg';
import { formatNumberWithCommas } from 'pubweb-smokey/dist/utils/formatters';
import ChevronLeftSvg from 'pubweb-smokey/dist/images/svg/iconography-16x16/chevron-lft.svg';
import { pushGTMEvent } from 'pubweb-smokey/dist/utils/analytics';
import { getLandById } from '@services/landService';
import { getBaseAwsApiUrl } from '@utils/config';
import { LandDetailsStyles } from './LandDetails.styled';
import Slideshow from '@components/Shared/Slideshow/Slideshow';
import MapComponent from '@components/Locations/MapComponent';
import LocationMarker from '@components/Locations/LocationMarker';
import { trackListHub } from '@utils/listHub';

const LandDetails = ({ ssrLandDetails }) => {
  const [landDetails] = useState(ssrLandDetails);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [hasImage, setHasImage] = useState([]);

  const mapOptions = {
    center: {
      lat: landDetails.geocodeLocation.latitude,
      lng: landDetails.geocodeLocation.longitude,
    },
    clickableIcons: false,
    draggable: true,
    draggableCursor: 'grab',
    draggingCursor: 'grabbing',
    scrollwheel: false,
    mapTypeControl: false,
    mapTypeControlOptions: {},
    streetViewControl: false,
    zoomControl: true,
    zoomControlOptions: {},
    zoom: 13,
    optimized: false,
  };

  const propertyListingsBackLink = () => {
    return `/find-land/listings/?zip=${landDetails.address.postalCode}&minPrice=0&maxPrice=${landDetails.listPrice.parsedValue}`;
  };

  useEffect(() => {
    landDetails.media.map(() => {
      setHasImage((state) => [...state, true]);
    });
  }, []);

  const handleImageError = (index) =>
    setHasImage((state) => [
      ...state.slice(0, index),
      false,
      ...state.slice(index + 1),
    ]);

  useEffect(() => {
    trackListHub('submit', 'DETAIL_PAGE_VIEWED', {
      lkey: landDetails.listingKey,
    });
  }, []);

  return (
    <LandDetailsStyles>
      <Head htmlAttributes={{ lang: 'en' }}>
        <title>{`Property Listing ${
          landDetails?.mlsNumber || landDetails?.listingKey
        } | Clayton`}</title>
        <meta
          name="description"
          content={`See details about ${
            landDetails?.mlsNumber || landDetails?.listingKey
          } property listing.`}
        />
        <meta
          property="og:image"
          content={
            getBaseAwsApiUrl() +
            'v1/property_listings/' +
            landDetails?.listingKey +
            '/media/shareable'
          }
        />
      </Head>
      <div className="constrained-width">
        <div className="land-search">
          <Link href={propertyListingsBackLink()}>
            <div>
              <ChevronLeftSvg />
            </div>{' '}
            Property Listings
          </Link>
        </div>
        <div className="land-top-section">
          <div className="land-slideshow">
            <Slideshow
              showThumbs={false}
              imageArray={landDetails.media.map((media, index) => {
                return media?.mediaUrl && hasImage[index] ? (
                  <AspectImage
                    containerOrientation="landscape"
                    key={'media-image-' + index}
                    behavior="fill-container"
                    aspectRatio={{
                      width: 525,
                      height: 343,
                    }}
                    src={media.mediaUrl + '?w=600'}
                    alt="Property Photo"
                    onError={() => handleImageError(index)}
                  />
                ) : (
                  <div
                    key={'media-image-' + index}
                    className="image-unavailable"
                  >
                    Image unavailable
                  </div>
                );
              })}
            />
            <div className="land-image-count">
              <span className="caption">{landDetails.media.length}</span>
              <CameraSvg viewBox="0 0 16 16" />
            </div>
          </div>
          <div className="land-info">
            <div className="land-header-info">
              {landDetails.listingStatus && (
                <span
                  className={`status-indicator label ${
                    landDetails.listingStatus === 'Active'
                      ? 'active'
                      : 'pending'
                  }`}
                >
                  {landDetails.listingStatus === 'Active'
                    ? 'Active'
                    : 'Pending'}
                </span>
              )}
              <h2>
                <div>
                  {landDetails.address.fullStreetAddress &&
                    landDetails.address.fullStreetAddress}
                </div>
                <div>
                  {landDetails.address.city && landDetails.address.city + ', '}
                  {landDetails.address.stateOrProvince
                    ? landDetails.address.stateOrProvince + ' '
                    : null}
                  {landDetails.address.postalCode
                    ? landDetails.address.postalCode
                    : null}
                </div>
              </h2>

              {landDetails.mlsNumber && (
                <span className="label large">
                  MLS#: {landDetails.mlsNumber}
                </span>
              )}

              {landDetails.listPrice.value && (
                <h5>{`$${formatNumberWithCommas(landDetails.listPrice.value)}`}</h5>
              )}
            </div>

            <div className="land-specs">
              {landDetails.lotSize && (
                <div className="land-spec">
                  <div>
                    <HouseSvg viewBox="0 0 48 48" />
                  </div>
                  <span className="standard">
                    {formatNumberWithCommas(
                      parseFloat(landDetails.lotSize.value).toFixed(2)
                    )}{' '}
                    {landDetails.lotSize.lotSizeUnits} (Lot/Plot)
                  </span>
                </div>
              )}

              {landDetails.listingDate ? (
                <div className="land-spec calendar">
                  <div>
                    <CalendarSvg viewBox="0 0 48 48" />
                  </div>
                  {/* if daysPosted is a negative number say "Coming Soon" */}
                  <span className="standard">
                    {landDetails.daysPosted > 0
                      ? 'Posted ' + landDetails.daysPosted + ' Days Ago'
                      : 'Coming Soon'}
                  </span>
                </div>
              ) : null}

              {landDetails.livingArea ? (
                <div className="land-spec">
                  {/* <MountainsSvg /> */}
                  <span className="standard">
                    {formatNumberWithCommas(landDetails.livingArea)} (Living
                    Space)
                  </span>
                </div>
              ) : null}

              {landDetails.bedrooms ? (
                <div className="land-spec">
                  <div>
                    <BedroomSvg viewBox="0 0 48 48" />
                  </div>
                  <span className="standard">{landDetails.bedrooms} beds</span>
                </div>
              ) : null}

              {landDetails.bathrooms ? (
                <div className="land-spec">
                  <div>
                    <BathroomSvg viewBox="0 0 48 48" />
                  </div>
                  <span className="standard">
                    {landDetails.bathrooms} baths
                  </span>
                </div>
              ) : null}
            </div>
          </div>
        </div>
        <div className="land-description">
          <h6>PROPERTY INFO</h6>
          <div className="land-redundant-address">
            <h2>
              {landDetails.address.fullStreetAddress &&
                landDetails.address.fullStreetAddress}
            </h2>
            <h4>
              {landDetails.address.city && landDetails.address.city + ', '}
              {landDetails.address.stateOrProvince &&
                landDetails.address.stateOrProvince + ' '}
              {landDetails.address.postalCode &&
                landDetails.address.postalCode + ' '}
            </h4>
          </div>
          {landDetails.listingDescription &&
          (showFullDescription ||
            landDetails.listingDescription.length <= 300) ? (
            <p>{landDetails.listingDescription}</p>
          ) : landDetails.listingDescription ? (
            <p>
              {truncateText(landDetails.listingDescription, 300)}
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setShowFullDescription(true);
                }}
              >
                {' '}
                Read More
              </a>
            </p>
          ) : null}
        </div>
        <div className="land-location">
          <h6>LOCATION</h6>
          <div className="land-map">
            <MapComponent mapOptions={mapOptions}>
              <LocationMarker location={landDetails.geocodeLocation} />
            </MapComponent>
          </div>
        </div>
      </div>
      <div className="land-bottom-section">
        <div className="constrained-width">
          <div className="land-more-info">
            <h2>Learn More About This Property</h2>
            <h5 className="land-broker">
              {(landDetails.listingParticipants[0].firstName ||
                landDetails.listingParticipants[0].lastName) &&
                landDetails.listingParticipants[0].firstName +
                  ' ' +
                  landDetails.listingParticipants[0].lastName +
                  ', '}
              {landDetails.brokerage &&
                landDetails.brokerage.name &&
                landDetails.brokerage.name + ', '}
              {landDetails.offices[0] && landDetails.offices[0].name}
            </h5>
            <div>
              {landDetails.brokerage && landDetails.brokerage.phone ? (
                <h5>
                  Brokerage Phone:{' '}
                  <a
                    onClick={() =>
                      pushGTMEvent('Link', 'Listing - Brokerage Phone')
                    }
                    className="tel"
                    href={`tel:${landDetails.brokerage.phone}`}
                  >
                    {landDetails.brokerage.phone}
                  </a>
                  <br />
                </h5>
              ) : null}

              {landDetails.listingParticipants[0] &&
              landDetails.listingParticipants[0].primaryContactPhone ? (
                <h5>
                  Listing Agent Phone:{' '}
                  <a
                    onClick={() =>
                      pushGTMEvent('Link', 'Listing - Agent Phone')
                    }
                    className="tel"
                    href={`tel:${landDetails.listingParticipants[0].primaryContactPhone}`}
                  >
                    {landDetails.listingParticipants[0].primaryContactPhone}
                  </a>
                  <br />
                </h5>
              ) : null}

              {(!landDetails.brokerage ||
                landDetails.brokerage.phone ||
                !landDetails.listingParticipants[0] ||
                !landDetails.listingParticipants[0].primaryContactPhone) &&
              landDetails.listingParticipants[0].officePhone ? (
                <h5>
                  Listing Office Phone Number:{' '}
                  <a
                    onClick={() =>
                      pushGTMEvent('Link', 'Listing - Office Phone')
                    }
                    className="tel"
                    href={`tel:${landDetails.listingParticipants[0].officePhone}`}
                  >
                    {landDetails.listingParticipants[0].officePhone}
                  </a>
                  <br />
                </h5>
              ) : null}

              {landDetails.listingParticipants[0].email ? (
                <h5>
                  Listing Agent Email:{' '}
                  <a
                    onClick={() =>
                      pushGTMEvent('Link', 'Listing - Agent Email')
                    }
                    href={`mailto:${landDetails.listingParticipants[0].email}`}
                  >
                    {landDetails.listingParticipants[0].email}
                  </a>
                  <br />
                </h5>
              ) : landDetails.brokerage && landDetails.brokerage.email ? (
                <span>
                  Brokerage Email:
                  <a
                    onClick={() =>
                      pushGTMEvent('Link', 'Listing - Brokerage Email')
                    }
                    href={`mailto:${landDetails.brokerage.email}`}
                  >
                    {landDetails.brokerage.email}
                  </a>
                  <br />
                </span>
              ) : null}

              {landDetails.listingUrl ? (
                <span className="more-details">
                  <a
                    onClick={() =>
                      pushGTMEvent('Link', 'Open Listing Agent Page')
                    }
                    target="_blank"
                    className="listing-link"
                    href={landDetails.listingUrl}
                    rel="noreferrer"
                  >
                    More Details
                  </a>
                </span>
              ) : null}
            </div>
          </div>
          <div className="land-disclaimer">
            {landDetails.disclaimer && (
              <div className="disclaimer">
                {landDetails.disclaimer.value ? (
                  <div className="mls-disclaimer-container">
                    <span className="caption">
                      {landDetails.disclaimer.value}
                      <br />
                    </span>
                  </div>
                ) : null}
                <Link href="/find-land/terms-of-use">
                  ListHub Data Terms of Use
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </LandDetailsStyles>
  );
};

LandDetails.propTypes = {
  ssrLandDetails: PropTypes.any,
};

export async function getServerSideProps({ params }) {
  let landDetails = null;

  try {
    const result = await getLandById(params.listingKey);
    landDetails = result;
  } catch (e) {
    console.log(e);
    if (e?.response?.status === 400) {
      return {
        redirect: {
          destination: '/find-land',
          permanent: false,
        },
      };
    }
  }

  if (landDetails == null) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      ssrLandDetails: landDetails,
    },
  };
}

export default LandDetails;
